<template>
  <div>
    <div v-if="answers && answers.length > 0" class="survey-question" :class="additionalClass">
      <h2 :class="`${isTheRiseFrameworkSurvey ? 'mainColor--text' : ''} survey-question__title`">{{ title }}</h2>
      <p class="survey-question__description" v-if="description.length > 0">{{ description }}</p>
      <v-radio-group
        v-model="answerModels[id]"
        v-if="type === SURVEY_QUESTION_TYPES.SINGLE_CHOICE"
        @change="onChangeRadio"
      >
        <v-row>
          <v-col
            cols="12"
            v-for="answer in answers"
            :key="`answer-radio-col-${answer ? answer.id : 'no-answer'}`"
          >
            <template v-if="answer.type === SURVEY_ANSWER_TYPES.RADIO">
              <v-radio
                v-model="answer.id"
                :key="`answer-${answer.id}`"
                :value="answer.id"
                :label="answer.title || null"
                :disabled="disabledAnswersOnlyForThisQuestion"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                class="survey-question__answer survey-question__answer--radio"
              />
            </template>
            <template v-else-if="answer.type === SURVEY_ANSWER_TYPES.TEXT">
              <v-radio
                :key="`answer-radio-${answer.id}`"
                :value="`virtual-answer-${answer.id}`"
                :label="answer.title || null"
                :disabled="disabledAnswersOnlyForThisQuestion"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                class="survey-question__answer survey-question__answer--radio"
              />
              <bh-text-input
                higher
                inputBackgroundClass="bhWhite"
                roundedClass="rounded"
                v-model="answerModels[answer.id]"
                :class="{
                  'd-none': answerModels[id] !== `virtual-answer-${answer.id}`,
                }"
                :key="`answer-${answer.id}`"
                :no-margin="true"
                :value="answer.content"
                :label="answer.placeholder || answer.title || null"
                :disabled="disabledAnswers"
                :readonly="disabledAnswers"
                @input-text="value => onChangeText(answer.id, value, answer.id)"
              />
            </template>
            <template v-else-if="answer.type === SURVEY_ANSWER_TYPES.TEXTAREA">
              <v-radio
                :key="`answer-radio-${answer.id}`"
                :value="`virtual-answer-${answer.id}`"
                :label="answer.title || null"
                :disabled="disabledAnswersOnlyForThisQuestion"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                class="survey-question__answer survey-question__answer--radio"
              />
              <v-textarea
                :counter="answer.maxLength || undefined"
                v-model="answerModels[answer.id]"
                background-color="#fff"
                :label="answer.placeholder || answer.title || null"
                :value="answer.content"
                :disabled="disabledAnswers"
                :readonly="disabledAnswers"
                :class="{
                  'd-none': answerModels[id] !== `virtual-answer-${answer.id}`,
                }"
                :key="`answer-${answer.id}`"
                dense
                filled
                @input="value => onChangeText(answer.id, value, answer.id)"
              />
            </template>
            <template v-else-if="answer.type === SURVEY_ANSWER_TYPES.NUMBER">
              <v-radio
                :key="`answer-radio-${answer.id}`"
                :value="`virtual-answer-${answer.id}`"
                :label="answer.title || null"
                :disabled="disabledAnswersOnlyForThisQuestion"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                class="survey-question__answer survey-question__answer--radio"
              />
              <bh-number-input
                v-model="answerModels[answer.id]"
                :class="{
                  'd-none': answerModels[id] !== `virtual-answer-${answer.id}`,
                }"
                :key="`answer-${answer.id}`"
                :label="answer.placeholder || answer.title || null"
                :value="answer.value"
                :minNumber="answer.minValue || 0"
                :maxNumber="answer.maxValue || 100000"
                :allowEmpty="true"
                :readonly="disabledAnswers"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                @input="value => onChangeNumber(answer.id, value)"
              />
            </template>
          </v-col>
          <v-col v-if="!questionAnswered && required && showInputRequiredError" cols="12"><span class="bhRed--text mb-4 d-inline-block confirmation-error" :key="`error-required-${id}`">Field is required</span></v-col>
        </v-row>
      </v-radio-group>
      <div
        class="survey-question__checkbox"
        v-else-if="type === SURVEY_QUESTION_TYPES.MULTI_CHOICE"
      >
        <v-row>
          <v-col
            cols="12"
            v-for="answer in answers"
            :key="`answer-${answer ? answer.id : 'no-answer'}`"
          >
            <template v-if="answer.type === SURVEY_ANSWER_TYPES.CHECKBOX">
              <v-checkbox
                v-model="answerModels[answer.id]"
                class="survey-question__answer survey-question__answer--checkbox"
                :hide-details="true"
                :label="answer.title || null"
                :disabled="disabledAnswersOnlyForThisQuestion"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                @change="value => onChangeCheckbox(answer.id, value)"
              />
            </template>
            <template v-else-if="answer.type === SURVEY_ANSWER_TYPES.TEXT">
              <v-checkbox
                v-model="answerModels[answer.id]"
                :key="`answer-checkbox-${answer.id}`"
                :value="`virtual-answer-${answer.id}`"
                class="survey-question__answer survey-question__answer--checkbox"
                :hide-details="true"
                :label="answer.title || null"
                :disabled="disabledAnswersOnlyForThisQuestion"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                @change="value => onChangeCheckbox(answer.id, value)"
              />
              <bh-text-input
                higher
                inputBackgroundClass="bhWhite"
                roundedClass="rounded"
                v-model="answerModels[`virtual-input-${answer.id}`]"
                :class="{
                  'd-none': answerModels[answer.id] !== `virtual-answer-${answer.id}`,
                }"
                :key="`answer-${answer.id}`"
                :no-margin="true"
                :value="answer.content"
                :label="answer.placeholder || answer.title || null"
                :disabled="disabledAnswers"
                :readonly="disabledAnswers"
                @input-text="value => onChangeText(answer.id, value, `virtual-input-${answer.id}`)"
              />
            </template>
            <template v-else-if="answer.type === SURVEY_ANSWER_TYPES.TEXTAREA">
              <v-checkbox
                v-model="answerModels[answer.id]"
                :key="`answer-checkbox-${answer.id}`"
                :value="`virtual-answer-${answer.id}`"
                class="survey-question__answer survey-question__answer--checkbox"
                :hide-details="true"
                :label="answer.title || null"
                :disabled="disabledAnswersOnlyForThisQuestion"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                @change="value => onChangeCheckbox(answer.id, value)"
              />
              <v-textarea
                :counter="answer.maxLength || undefined"
                background-color="#fff"
                v-model="answerModels[`virtual-input-${answer.id}`]"
                :label="answer.placeholder || answer.title || null"
                :value="answer.content"
                :disabled="disabledAnswers"
                :readonly="disabledAnswers"
                :class="{
                  'd-none': answerModels[id] !== `virtual-answer-${answer.id}`,
                }"
                :key="`answer-${answer.id}`"
                dense
                filled
                @input="value => onChangeText(answer.id, value, `virtual-input-${answer.id}`)"
              />
            </template>
            <template v-else-if="answer.type === SURVEY_ANSWER_TYPES.NUMBER">
              <v-checkbox
                v-model="answerModels[answer.id]"
                :key="`answer-checkbox-${answer.id}`"
                :value="`virtual-answer-${answer.id}`"
                class="survey-question__answer survey-question__answer--checkbox"
                :hide-details="true"
                :label="answer.title || null"
                :disabled="disabledAnswersOnlyForThisQuestion"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                @change="value => onChangeCheckbox(answer.id, value)"
              />
              <bh-number-input
                v-model="answerModels[`virtual-input-${answer.id}`]"
                :class="{
                  'd-none': answerModels[answer.id] !== `virtual-answer-${answer.id}`,
                }"
                :key="`answer-${answer.id}`"
                :label="answer.placeholder || answer.title || null"
                :value="answer.value"
                :minNumber="answer.minValue || 0"
                :maxNumber="answer.maxValue || 100000"
                :allowEmpty="true"
                :readonly="disabledAnswers"
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                @input="value => onChangeNumber(answer.id, value)"
              />
            </template>
          </v-col>
          <v-col v-if="!questionAnswered && required && showInputRequiredError" cols="12"><span class="bhRed--text mb-4 d-inline-block confirmation-error" :key="`error-required-${id}`">Field is required</span></v-col>
        </v-row>
      </div>
      <template v-else-if="type === SURVEY_QUESTION_TYPES.TEXT">
        <template v-for="answer in answers">
          <template v-if="answer.type === SURVEY_ANSWER_TYPES.TEXT">
            <bh-text-input
              v-if="isConfirmation"
              v-model="confirmationAnswers[answer.id]"
              higher
              inputBackgroundClass="bhWhite"
              roundedClass="rounded"
              :key="`answer-${answer.id}`"
              class="mb-4"
              :no-margin="true"
              :value="answer.content"
              :label="answer.title || null"
              :disabled="disabledAnswers"
              :readonly="disabledAnswers"
              @input-text="value => onChangeText(answer.id, value, answer.id, isConfirmation)"
            />
            <bh-text-input
              v-else
              v-model="answerModels[answer.id]"
              higher
              inputBackgroundClass="bhWhite"
              roundedClass="rounded"
              :key="`answer-${answer.id}`"
              class="mb-4"
              :no-margin="true"
              :value="answer.content"
              :label="answer.title || null"
              :disabled="disabledAnswers"
              :readonly="disabledAnswers"
              @input-text="value => onChangeText(answer.id, value, answer.id)"
            />
          </template>
          <template v-else-if="answer.type === SURVEY_ANSWER_TYPES.TEXTAREA">
            <v-textarea
              v-if="isConfirmation"
              :counter="answer.maxLength || undefined"
              v-model="confirmationAnswers[answer.id]"
              background-color="#fff"
              :label="answer.title || null"
              :value="answer.content"
              :disabled="disabledAnswers"
              :readonly="disabledAnswers"
              :key="`answer-${answer.id}`"
              class="mb-4"
              dense
              filled
              @input="value => onChangeText(answer.id, value, answer.id, isConfirmation)"
            />
            <v-textarea
              v-else
              :counter="answer.maxLength || undefined"
              v-model="answerModels[answer.id]"
              background-color="#fff"
              :label="answer.title || null"
              :value="answer.content"
              :disabled="disabledAnswers"
              :readonly="disabledAnswers"
              :key="`answer-${answer.id}`"
              class="mb-4"
              dense
              filled
              @input="value => onChangeText(answer.id, value, answer.id)"
            />
          </template>
          <span class="bhRed--text mb-4 d-inline-block confirmation-error" :key="`error-${answer.id}-${isConfirmationAnswerValid[answer.id]}`" v-if="isConfirmation && !isConfirmationAnswerValid[answer.id]">Fields do not match.</span>
          <span v-if="!questionAnswered && required && showInputRequiredError && !(isConfirmation && !isConfirmationAnswerValid[answer.id])" class="bhRed--text mb-4 d-inline-block confirmation-error" :key="`error-required-${id}-${answer.id}`">Field is required</span>
          <survey-question
          :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
            v-if="!isConfirmation && answer.confirmation"
            :is-confirmation="true"
            :confirmations-original-values.sync="answerModels"
            :key="`answer-${answer.id}-confirmation`"
            :id="id"
            :title="answer.confirmation"
            :answers="answers"
            :type="type"
            :required.sync="required"
            :disabled-answers="disabledAnswers"
            :show-input-required-error.sync="showInputRequiredError"
            @handleChangeAnswer="handleChangeAnswer"
            additional-class="ml-0"
          />
        </template>
      </template>
      <template v-else-if="type === SURVEY_QUESTION_TYPES.NUMBER">
        <bh-number-input
          v-for="answer in answers"
          v-model="answerModels[answer.id]"
          inputBackgroundClass="bhWhite"
          :key="`answer-${answer.id}`"
          :label="answer.title || null"
          :value="answer.value ? answer.value : answer.minValue"
          :minNumber="answer.minValue || 0"
          :maxNumber="answer.maxValue || 100000"
          :allowEmpty="true"
          :readonly="disabledAnswers"
          class="survey-question__answer--number"
          :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
          @input="value => onChangeNumber(answer.id, value)"
        />
        <span v-if="!questionAnswered && required && showInputRequiredError" class="bhRed--text mb-4 d-inline-block confirmation-error" :key="`error-required-${id}`">Field is required</span>
      </template>
      <template v-else-if="type === SURVEY_QUESTION_TYPES.DROPDOWN_LIST">
        <bh-select
          v-model="answerModels[id]"
          :disabled="disabledAnswersOnlyForThisQuestion"
          :items="answers"
          item-text="title"
          item-value="id"
          class="bhWhite my-4 px-4 rounded-pill survey-question__answer--select"
          :hide-details="true"
          @input="onChangeSelect(id, $event)"
        />
        <survey-question
        :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
          v-if="(answers[answerModels[id]] || {}).embeddedQuestion"
          :id="(answers[answerModels[id]] || {}).embeddedQuestion.id"
          :title="(answers[answerModels[id]] || {}).embeddedQuestion.title"
          :description="(answers[answerModels[id]] || {}).embeddedQuestion.description"
          :answers="(answers[answerModels[id]] || {}).embeddedQuestion.answers"
          :type="(answers[answerModels[id]] || {}).embeddedQuestion.type"
          :required.sync="(answers[answerModels[id]] || {}).embeddedQuestion.required"
          :parentQuestionId="id"
          :parentAnswerId="(answers[answerModels[id]] || {}).id"
          :rootParentQuestionId="parentQuestionId"
          :rootParentAnswerId="parentAnswerId"
          :disabled-answers="disabledAnswers"
          :show-input-required-error.sync="showInputRequiredError"
          @handleChangeAnswer="handleChangeAnswer"
        />
        <span v-if="!questionAnswered && required && showInputRequiredError" class="bhRed--text mb-4 d-inline-block confirmation-error" :key="`error-required-${id}`">Field is required</span>
      </template>
      <div v-else></div>
    </div>

    <template v-for="mainAnswer in embeddedQuestions">
      <survey-question
        :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
        :key="`answer-${mainAnswer.id}-embedded`"
        :id="mainAnswer.embeddedQuestion.id"
        :title="mainAnswer.embeddedQuestion.title"
        :description="mainAnswer.embeddedQuestion.description"
        :answers="mainAnswer.embeddedQuestion.answers"
        :type="mainAnswer.embeddedQuestion.type"
        :required.sync="mainAnswer.embeddedQuestion.required"
        :parentQuestionId="id"
        :parentAnswerId="mainAnswer.id"
        :rootParentQuestionId="parentQuestionId"
        :rootParentAnswerId="parentAnswerId"
        :disabled-answers="disabledAnswers"
        :show-input-required-error.sync="showInputRequiredError"
        @handleChangeAnswer="handleChangeAnswer"
      />
    </template>
  </div>
</template>

<script>
import { SURVEY_ANSWER_TYPES, SURVEY_QUESTION_TYPES } from '@/helpers/constants'
import BhTextInput from '@/components/inputs/BhTextInput.vue'
import BhNumberInput from '@/components/inputs/BhNumberInput.vue'
import BhSelect from '@/components/inputs/BhSelect.vue'
import { mapActions, mapState } from 'vuex'
import { checkIfAnswered } from '@/helpers/screenerCheckIfAnswered'

export default {
  name: 'SurveyQuestion',
  components: {
    BhTextInput,
    BhNumberInput,
    BhSelect,
  },
  mixins: [],
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
      validator: function(value) {
        // Check if type is supported
        return !!SURVEY_QUESTION_TYPES[value]
      },
    },
    required: {
      type: Boolean,
      default: false
    },
    parentQuestionId: {
      type: [String, null],
      default: null
    },
    parentAnswerId: {
      type: [String, null],
      default: null
    },
    rootParentQuestionId: {
      type: [String, null],
      default: null
    },
    rootParentAnswerId: {
      type: [String, null],
      default: null
    },
    answers: {
      type: Array,
      default: () => [],
    },
    disabledAnswers: {
      type: Boolean,
      default: false
    },
    isConfirmation: {
      type: Boolean,
      default: false
    },
    additionalClass: {
      type: String,
      default: ''
    },
    disabledQuestions: {
      type: Array,
      default: () => []
    },
    showInputRequiredError: {
      type: Boolean,
      default: false
    },
    confirmationsOriginalValues: {
      type: Object,
      default: () => ({}),
    },
    isTheRiseFrameworkSurvey: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    let answerModels = {}

    if (this.$props.type === SURVEY_QUESTION_TYPES.SINGLE_CHOICE) {
      const selectedAnswer = this.answers.find(answer => answer.selected)
      answerModels[this.id] = selectedAnswer
        ? selectedAnswer.type === SURVEY_ANSWER_TYPES.TEXT ||
          selectedAnswer.type === SURVEY_ANSWER_TYPES.TEXTAREA ||
          selectedAnswer.type === SURVEY_ANSWER_TYPES.NUMBER
          ? `virtual-answer-${selectedAnswer.id}`
          : selectedAnswer?.id || null
        : null
      const textNumberAnswers = this.answers.filter(
        answer =>
          answer.type !== SURVEY_ANSWER_TYPES.RADIO &&
          answer.type !== SURVEY_ANSWER_TYPES.CHECKBOX,
      )
      textNumberAnswers.forEach(answer => {
        answerModels[answer.id] =
          answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA
            ? answer.content
            : answer.value
      })
    } else if (this.$props.type === SURVEY_QUESTION_TYPES.MULTI_CHOICE) {
      this.answers.forEach(answer => {

        answerModels[answer.id] = answer.selected
          ? answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA || answer.type === SURVEY_ANSWER_TYPES.NUMBER
            ? `virtual-answer-${answer.id}`
            : answer.selected || null
          : null

        if (answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA) {
          answerModels[`virtual-input-${answer.id}`] = answer.content
        }

        if (answer.type === SURVEY_ANSWER_TYPES.NUMBER) {
          answerModels[`virtual-input-${answer.id}`] = answer.value
        }
      })
    } else if (this.$props.type === SURVEY_QUESTION_TYPES.DROPDOWN_LIST) {
      const selectedAnswer = this.answers.find(answer => answer.selected);
      answerModels[this.id] = selectedAnswer?.id || null
    } else {
      this.answers.forEach(answer => {
        let value = answer.selected
        if (answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA) value = answer.content
        else if (answer.type === SURVEY_ANSWER_TYPES.NUMBER)
          value = answer.value
        else value = answer.selected

        answerModels[answer.id] = value
      })
    }

    return {
      confirmationValid: [],
      SURVEY_QUESTION_TYPES,
      SURVEY_ANSWER_TYPES,
      answerModels,
    }
  },
  computed: {
    ...mapState('surveyStates', ['confirmationAnswers']),
    embeddedQuestions() {
      let embeddedQuestions = []
      const allAnswers = [...this.answers]
      if (!allAnswers || allAnswers.length < 1) return embeddedQuestions
      for (const answer of allAnswers) {
        if(this.type === SURVEY_QUESTION_TYPES.SINGLE_CHOICE) {
          if(answer.type === SURVEY_ANSWER_TYPES.RADIO && answer.embeddedQuestion && this.answerModels[this.id] === answer.id) {
            embeddedQuestions = [...embeddedQuestions, answer]
          }
          if((answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA || answer.type === SURVEY_ANSWER_TYPES.NUMBER) && answer.embeddedQuestion && this.answerModels[this.id] === `virtual-answer-${answer.id}`) {
            embeddedQuestions = [...embeddedQuestions, answer]
          }
        }
        if(this.type === SURVEY_QUESTION_TYPES.MULTI_CHOICE) {
          if(answer.type === SURVEY_ANSWER_TYPES.CHECKBOX && answer.embeddedQuestion && this.answerModels[answer.id] === true){
            embeddedQuestions = [...embeddedQuestions, answer]
          }
          if((answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA || answer.type === SURVEY_ANSWER_TYPES.NUMBER) && answer.embeddedQuestion && this.answerModels[answer.id] === `virtual-answer-${answer.id}`) {
            embeddedQuestions = [...embeddedQuestions, answer]
          }
        }
        if(this.type === SURVEY_QUESTION_TYPES.TEXT && answer.embeddedQuestion && this.answerModels[answer.id] && this.answerModels[answer.id] !== '') {
          embeddedQuestions = [...embeddedQuestions, answer]
        }
        if(this.type === SURVEY_QUESTION_TYPES.NUMBER && answer.embeddedQuestion && this.answerModels[answer.id] >= answer.minValue && this.answerModels[answer.id] <= answer.maxValue) {
          embeddedQuestions = [...embeddedQuestions, answer]
        }
      }
      return embeddedQuestions
    },
    isConfirmationAnswerValid() {
      let validConfirmations = {}
      if (!this.isConfirmation) return validConfirmations

      for (const answer of this.answers) {
        const validAnswer = this.confirmationsOriginalValues[answer.id] === this.confirmationAnswers[answer.id]
        validConfirmations = {...validConfirmations, [answer.id]: validAnswer }
      }

      return validConfirmations
    },
    disabledAnswersOnlyForThisQuestion() {
      return this.disabledAnswers || this.disabledQuestions.includes(this.id)
    },
    questionAnswered() {
      const { id, type, answers } = this
      return checkIfAnswered({ id, type, answers }, true)
    },
  },
  methods: {
    ...mapActions('surveyStates', ['updateConfirmationAnswer']),
    async getAllAnswersData() {
      const allAnswers = {}
      for (const [_, answer] of this.answers.entries()) {
        let selected = false
        let value = null
        let content = null

        if (this.type === SURVEY_QUESTION_TYPES.SINGLE_CHOICE) {
          selected = (answer.type === SURVEY_ANSWER_TYPES.NUMBER || answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA
            ? this.answerModels[this.id] === `virtual-answer-${answer.id}`
            : this.answerModels[this.id] === answer.id) || false
          value = answer.type === SURVEY_ANSWER_TYPES.NUMBER
            ? this.answerModels[answer.id]
            : null
          content = answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA
            ? this.answerModels[answer.id]
            : null
        } else if (this.type === SURVEY_QUESTION_TYPES.MULTI_CHOICE) {
          selected = (answer.type === SURVEY_ANSWER_TYPES.NUMBER || answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA
            ? this.answerModels[answer.id] === `virtual-answer-${answer.id}`
            : this.answerModels[answer.id] === true)
          value = answer.type === SURVEY_ANSWER_TYPES.NUMBER
            ? this.answerModels[answer.id]
            : null
          content = answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA
            ? this.answerModels[`virtual-input-${answer.id}`]
            : null
        } else if (this.type === SURVEY_QUESTION_TYPES.DROPDOWN_LIST) {
          selected = this.answerModels[this.id] === answer.id || false
          value = null
          content = null
        } else {
          selected = false
          value = answer.type === SURVEY_ANSWER_TYPES.NUMBER ? this.answerModels[answer.id] : null
          content = answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA ? this.answerModels[answer.id] : null
        }

        allAnswers[answer.id] = {
          selected: selected || false,
          value: value
            ? value
            : value === 0
              ? 0
              : null,
          content: content || null
        }
      }

      return await allAnswers
    },
    onChangeRadio(answerId) {
      const answer = this.answers.find(el => el.id === answerId)
      const data = {
        selected: true
      }
      if (answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA) {
        data.content = answer?.content
      }
      if (answer.type === SURVEY_ANSWER_TYPES.NUMBER) {
        data.value = answer?.value
      }
      this.onChangeAnswer(answer, data)
    },
    onChangeText(answerId, text, answerModelsIndex, isConfirmation) {
      if (isConfirmation) {
        this.updateConfirmationAnswer({ answerId, content: text})
        return
      }

      const answer = this.answers.find(el => el.id === answerId)

      this.answerModels[answerModelsIndex] = text

      // if question type is single choice, then set selecten on true
      this.onChangeAnswer(answer, {
        selected: this.type === SURVEY_QUESTION_TYPES.SINGLE_CHOICE || this.type === SURVEY_QUESTION_TYPES.MULTI_CHOICE,
        content: text,
      })
    },
    onChangeNumber(answerId, number) {
      const answer = this.answers.find(el => el.id === answerId)

      // if question type is single choice, then set selecten on true
      this.onChangeAnswer(answer, {
        selected: this.type === SURVEY_QUESTION_TYPES.SINGLE_CHOICE || this.type === SURVEY_QUESTION_TYPES.MULTI_CHOICE,
        value: Number(number),
      })
    },
    onChangeCheckbox(answerId, selected) {
      const answer = this.answers.find(el => el.id === answerId)

      const data = {
        selected: !!selected
      }

      if (answer.type === SURVEY_ANSWER_TYPES.TEXT || answer.type === SURVEY_ANSWER_TYPES.TEXTAREA) {
        data.content = answer?.content
      }
      if (answer.type === SURVEY_ANSWER_TYPES.NUMBER) {
        data.value = answer?.value
      }

      this.onChangeAnswer(answer, data)
    },
    onChangeSelect(questionId, answerId) {
      const answer = this.answers.find(el => el.id === answerId)
      this.onChangeAnswer(answer, { selected: true })
    },
    async deselectIfNeeded(answer) {
      if (!answer) return;

      const answerIsDeselectAll = answer.deselectAll === true
      const otherAnswers = this.answers.filter(el => el.id !== answer.id)

      if (answerIsDeselectAll) {
        for (const [_, stateAnswer] of otherAnswers.entries()) {
          this.answerModels[stateAnswer.id] = false
        }
      } else {
        const otherDeselectAllAnswers = otherAnswers.filter(el => el.deselectAll === true && el.id !== answer.id)
        for (const [_, stateAnswer] of otherDeselectAllAnswers.entries()) {
          this.answerModels[stateAnswer.id] = false
        }
      }

      return await true;
    },
    async onChangeAnswer(answer, data) {
      if (data.selected) {
        await this.deselectIfNeeded(answer)
      }

      const allAnswersData = await this.getAllAnswersData()
      this.handleChangeAnswer({
        question: {
          id: this.id,
          title: this.title,
          answers: this.answers,
          type: this.type,
        },
        answer,
        data,
        parentAnswerId: this.parentAnswerId,
        parentQuestionId: this.parentQuestionId,
        rootParentAnswerId: this.rootParentAnswerId,
        rootParentQuestionId: this.rootParentQuestionId,
        allAnswersData,
      })
    },
    async handleChangeAnswer({question, answer, data, parentAnswerId, parentQuestionId, rootParentAnswerId, rootParentQuestionId, allAnswersData}) {
      if (data.selected) {
        await this.deselectIfNeeded(answer)
      }

      const newAllAnswersData = allAnswersData || await this.getAllAnswersData()
      this.$emit('handleChangeAnswer', {
        question,
        answer,
        data,
        parentAnswerId,
        parentQuestionId,
        rootParentAnswerId,
        rootParentQuestionId,
        allAnswersData: newAllAnswersData
      })
    }
  },
}
</script>

<style lang="scss">
@import '@/styles/variables';

.survey-question {
  counter-increment: question-counter;

  & & {
    margin-left: 24px;
    margin-top: 24px;
  }

  &__title {
    font-size: 16px;
    line-height: 1.5rem;
    &:before {
      content: counter(question-counter) '. ';
    }
  }

  &__description {
    font-weight: 500;
  }

  &__checkbox {
    margin-bottom: 32px;
  }

  &__answer {
    &--radio {
      background-color: #fff; // @TODO: Change background color to variable
      padding: ($gutter * 0.75) $gutter; // @TODO Change paddings to variables
      border-radius: $border-radius-root2;
      font-weight: 600;
    }

    &--checkbox {
      background-color: #fff; // @TODO: Change background color to variable
      padding: ($gutter * 0.75) $gutter; // @TODO Change paddings to variables
      border-radius: $border-radius-root2;
      font-weight: 600;
    }

    &--number {
      margin: 16px 0 32px;

      &.rounded-pill {
        border-radius: $border-radius-root2 !important;
      }
    }

    &--select {
      &.rounded-pill {
        border-radius: $border-radius-root2 !important;

        .v-input {
          &__control {
            padding-top: $gutter / 4;
            padding-bottom: $gutter / 4;
            font-weight: 600;
          }
          &__slot:before {
            border-color: transparent !important;
          }
        }
      }
    }
  }
  .bh-text-input__input {
    font-size: 16px;
  }
  .confirmation-error {
    font-weight: 700;
  }
}
</style>
